<script>
import {
  buildEditor,
  EditorState,
  EditorView,
  MessageMarkdownSerializer,
  MessageMarkdownTransformer,
  messageSchema,
  Selection,
} from '@chatwoot/prosemirror-schema';
import {
  suggestionsPlugin,
  triggerCharacters,
} from '@chatwoot/prosemirror-schema/src/mentions/plugin';
import { capitalizeFirstLetterPlugins } from './plugins/capitalizeFirstLetterRule';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import CannedResponse from '../conversation/CannedResponse.vue';
import TagAgents from '../conversation/TagAgents.vue';
import VariableList from '../conversation/VariableList.vue';
import KeyboardEmojiSelector from './keyboardEmojiSelector.vue';

import {
  appendSignature,
  findNodeToInsertImage,
  getContentNode,
  insertAtCursor,
  isBodyEmptyOrSignatureOnly,
  removeSignature,
  scrollCursorIntoView,
  setURLWithQueryAndSize,
} from 'dashboard/helper/editorHelper';

const TYPING_INDICATOR_IDLE_TIME = 4000;
const MAXIMUM_FILE_UPLOAD_SIZE = 4;

import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  hasPressedCommandAndEnter,
  hasPressedEnterAndNotCmdOrShift,
} from 'shared/helpers/KeyboardHelpers';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';

import { createTypingIndicator } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import {
  MESSAGE_EDITOR_IMAGE_RESIZES,
  MESSAGE_EDITOR_MENU_OPTIONS,
} from 'dashboard/constants/editor';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
// Aliased because this file already defines a local MAXIMUM_FILE_UPLOAD_SIZE of
// 4 MB for the signature editor. A pasted conversation image is an attachment,
// so it gets the same 40 MB ceiling the attachment path uses.
import {
  MAXIMUM_FILE_UPLOAD_SIZE as MAXIMUM_ATTACHMENT_UPLOAD_SIZE,
  INLINE_EMBEDDABLE_IMAGE_TYPES,
} from 'shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';

const buildPlugins = (placeholder, plugins, methods, enabledMenuOptions) =>
  buildEditor({
    schema: messageSchema,
    placeholder,
    methods,
    plugins,
    enabledMenuOptions,
  });

const createState = (
  content,
  placeholder,
  plugins,
  methods,
  enabledMenuOptions
) => {
  return EditorState.create({
    doc: new MessageMarkdownTransformer(messageSchema).parse(content),
    plugins: buildPlugins(placeholder, plugins, methods, enabledMenuOptions),
  });
};

export default {
  name: 'WootMessageEditor',
  components: {
    TagAgents,
    CannedResponse,
    VariableList,
    KeyboardEmojiSelector,
  },
  mixins: [keyboardEventListenerMixins],
  props: {
    value: { type: String, default: '' },
    editorId: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    isPrivate: { type: Boolean, default: false },
    enableSuggestions: { type: Boolean, default: true },
    mentionItems: { type: Array, default: null },
    overrideLineBreaks: { type: Boolean, default: false },
    updateSelectionWith: { type: String, default: '' },
    enableVariables: { type: Boolean, default: false },
    // Opt-in allow-list of static variable keys for the {{ }} picker. Empty (the
    // default) preserves today's behavior — every static token is offered.
    // Threaded straight down to VariableList. Consumers use it to curate the
    // picker down to a relevant subset (e.g. the ongoing-campaign editor narrows
    // it to the useful contact tokens) — a UX filter, not a resolve/no-resolve
    // distinction; outgoing messages can still resolve more tokens server-side.
    variableKeys: { type: Array, default: () => [] },
    enableCannedResponses: { type: Boolean, default: true },
    autoCapitalizeFirstLetter: { type: Boolean, default: false },
    variables: { type: Object, default: () => ({}) },
    enabledMenuOptions: { type: Array, default: () => [] },
    signature: { type: String, default: '' },
    allowSignature: { type: Boolean, default: false },
    channelType: { type: String, default: '' },
    // Opt-in: paste an image straight into the body instead of letting it become
    // an attachment. Off by default because the cid rewrite that makes an inline
    // image survive delivery lives in ConversationReplyMailer — so only an email
    // channel with `inline_email_images` on may turn this on. Anywhere else
    // (Slack, web widget, private notes) the markdown would reach the contact as
    // a raw URL, and on an email channel without the feature it would reach them
    // as a remote <img> with no download link to fall back to.
    enableInlineImagePaste: { type: Boolean, default: false },
    // What this editor is composing for — the reply box passes its draft key,
    // which is conversation AND reply mode. Uploads resolve asynchronously and
    // this component is NOT remounted when the agent opens another conversation,
    // or switches between private note and public reply: it keeps composing, with
    // different content. Anything that lands after the key changed belongs to a
    // draft that is no longer on screen — and a picture pasted into a private note
    // reaching the public reply is the same accident as it reaching another
    // contact. Null for callers with nothing to switch between (signature,
    // campaigns, canned responses), where the comparison is always true and
    // behaviour is unchanged.
    contextKey: { type: [String, Number], default: null },
    showImageResizeToolbar: { type: Boolean, default: false },
    focusOnMount: { type: Boolean, default: true },
    height: { type: Number, default: null },
    minHeight: { type: Number, default: 5 },
    maxHeight: { type: [String, Number], default: '' },
  },
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
    } = useUISettings();

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
    };
  },
  data() {
    return {
      typingIndicator: createTypingIndicator(
        () => {
          this.$emit('typingOn');
        },
        () => {
          this.$emit('typingOff');
        },
        TYPING_INDICATOR_IDLE_TIME
      ),
      showUserMentions: false,
      showCannedMenu: false,
      showVariables: false,
      showEmojiMenu: false,
      mentionSearchKey: '',
      cannedSearchTerm: '',
      variableSearchTerm: '',
      emojiSearchTerm: '',
      editorView: null,
      range: null,
      state: undefined,
      isImageNodeSelected: false,
      toolbarPosition: { top: 0, left: 0 },
      sizes: MESSAGE_EDITOR_IMAGE_RESIZES,
      selectedImageNode: null,
    };
  },
  computed: {
    editorRoot() {
      return this.$refs.editorRoot;
    },
    contentFromEditor() {
      return MessageMarkdownSerializer.serialize(this.editorView.state.doc);
    },
    shouldShowVariables() {
      return this.enableVariables && this.showVariables && !this.isPrivate;
    },
    shouldShowCannedResponses() {
      return (
        this.enableCannedResponses && this.showCannedMenu && !this.isPrivate
      );
    },
    editorMenuOptions() {
      return this.enabledMenuOptions.length
        ? this.enabledMenuOptions
        : MESSAGE_EDITOR_MENU_OPTIONS;
    },
    plugins() {
      const basePlugins = this.autoCapitalizeFirstLetter
        ? capitalizeFirstLetterPlugins((state, from) =>
            this.isRemainderEmptyDraft(state, from)
          )
        : [];

      if (!this.enableSuggestions) {
        return basePlugins;
      }

      return [
        ...basePlugins,
        suggestionsPlugin({
          matcher: triggerCharacters('@'),
          onEnter: args => {
            this.showUserMentions = true;
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;

            this.mentionSearchKey = args.text;

            return false;
          },
          onExit: () => {
            this.mentionSearchKey = '';
            this.showUserMentions = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showUserMentions;
          },
        }),
        suggestionsPlugin({
          matcher: triggerCharacters('/'),
          suggestionClass: '',
          onEnter: args => {
            if (this.isPrivate) {
              return false;
            }
            this.showCannedMenu = true;
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;

            this.cannedSearchTerm = args.text;
            return false;
          },
          onExit: () => {
            this.cannedSearchTerm = '';
            this.showCannedMenu = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showCannedMenu;
          },
        }),
        suggestionsPlugin({
          matcher: triggerCharacters('{{'),
          suggestionClass: '',
          onEnter: args => {
            if (this.isPrivate) {
              return false;
            }
            this.showVariables = true;
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;

            this.variableSearchTerm = args.text;
            return false;
          },
          onExit: () => {
            this.variableSearchTerm = '';
            this.showVariables = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showVariables;
          },
        }),
        suggestionsPlugin({
          matcher: triggerCharacters(':', 2),
          suggestionClass: '',
          onEnter: args => {
            this.showEmojiMenu = true;
            this.emojiSearchTerm = args.text || '';
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;
            this.emojiSearchTerm = args.text;
            return false;
          },
          onExit: () => {
            this.emojiSearchTerm = '';
            this.showEmojiMenu = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showEmojiMenu;
          },
        }),
      ];
    },
    sendWithSignature() {
      if (this.allowSignature && !this.isPrivate && this.channelType) {
        return this.fetchSignatureFlagFromUISettings(this.channelType);
      }

      return false;
    },
  },
  watch: {
    height: {
      handler(newHeight) {
        const el = this.$refs.editorRoot?.querySelector('.ProseMirror');
        if (!el) return;
        if (newHeight !== null) {
          el.style.height = `${newHeight}px`;
          el.style.maxHeight = 'none';
          el.style.minHeight = '0';
        } else {
          el.style.height = '';
          el.style.maxHeight = '';
          el.style.minHeight = `${this.minHeight}rem`;
          // Manual control was released — reflow to fit the current content.
          this.resizeEditor();
        }
      },
      flush: 'post',
    },
    maxHeight() {
      // Re-clamp when the available viewport height changes (window resize).
      this.resizeEditor();
    },
    showUserMentions(updatedValue) {
      this.$emit(
        'toggleUserMention',
        (this.isPrivate || !!this.mentionItems) && updatedValue
      );
    },
    showCannedMenu(updatedValue) {
      this.$emit('toggleCannedMenu', !this.isPrivate && updatedValue);
    },
    showVariables(updatedValue) {
      this.$emit('toggleVariablesMenu', !this.isPrivate && updatedValue);
    },
    value(newVal = '') {
      if (newVal !== this.contentFromEditor) {
        this.reloadState(newVal);
      }
    },
    editorId() {
      this.showCannedMenu = false;
      this.showEmojiMenu = false;
      this.showVariables = false;
      this.cannedSearchTerm = '';
      this.reloadState(this.value);
    },
    isPrivate() {
      this.reloadState(this.value);
    },
    placeholder() {
      this.reloadPlaceholder();
    },
    updateSelectionWith(newValue, oldValue) {
      if (!this.editorView) {
        return null;
      }
      if (newValue !== oldValue) {
        if (this.updateSelectionWith !== '') {
          const node = this.editorView.state.schema.text(
            this.updateSelectionWith
          );
          const tr = this.editorView.state.tr.replaceSelectionWith(node);
          this.editorView.focus();
          this.state = this.editorView.state.apply(tr);
          this.editorView.updateState(this.state);
          this.emitOnChange();
          this.$emit('clearSelection');
        }
      }
      return null;
    },
    sendWithSignature(newValue) {
      if (this.allowSignature) {
        this.toggleSignatureInEditor(newValue);
      }
    },
  },
  created() {
    this.statePlaceholder = this.placeholder;
    this.state = createState(
      this.value,
      this.placeholder,
      this.plugins,
      { onImageUpload: this.openFileBrowser },
      this.editorMenuOptions
    );
  },
  mounted() {
    this.createEditorView();
    this.editorView.updateState(this.state);
    // Size the editor to any pre-filled content (e.g. a restored draft).
    this.resizeEditor();
    if (this.focusOnMount) {
      this.focusEditorInputField();
    }

    this.$emitter.on(
      BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
      this.insertContentIntoEditor
    );
  },
  beforeDestroy() {
    this.$emitter.off(
      BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
      this.insertContentIntoEditor
    );
  },
  methods: {
    reloadState(content = this.value) {
      this.statePlaceholder = this.placeholder;
      this.state = createState(
        content,
        this.placeholder,
        this.plugins,
        { onImageUpload: this.openFileBrowser },
        this.editorMenuOptions
      );
      this.editorView.updateState(this.state);

      // Content changed programmatically (send/clear, canned insert, draft
      // restore) without a transaction, so re-measure the auto-grow height —
      // otherwise the editor stays at its previous expanded height until the
      // user types again.
      this.resizeEditor();

      this.focusEditor(content);
    },
    reloadPlaceholder() {
      if (!this.editorView || this.statePlaceholder === this.placeholder) {
        return;
      }
      this.statePlaceholder = this.placeholder;
      this.state = this.editorView.state.reconfigure({
        plugins: buildPlugins(
          this.placeholder,
          this.plugins,
          { onImageUpload: this.openFileBrowser },
          this.editorMenuOptions
        ),
      });
      this.editorView.updateState(this.state);
      if (this.height === null) this.resizeEditor();
    },
    focusEditor(content) {
      if (this.isBodyEmpty(content) && this.sendWithSignature) {
        this.handleEmptyBodyWithSignature();
      } else if (this.focusOnMount) {
        this.focusEditorInputField('end');
      }
    },
    toggleSignatureInEditor(signatureEnabled) {
      if (signatureEnabled) {
        this.addSignature();
      } else {
        this.removeSignature();
      }
    },
    addSignature() {
      let content = this.value;
      const contentWasEmpty = this.isBodyEmpty(content);
      content = appendSignature(content, this.signature);
      this.reloadState(content);

      if (contentWasEmpty) {
        this.handleEmptyBodyWithSignature();
      }
    },
    removeSignature() {
      if (!this.signature) return;
      let content = this.value;
      content = removeSignature(content, this.signature);
      this.reloadState(content);
    },
    isRemainderEmptyDraft(state, from) {
      const remainder = state.doc.slice(from);
      if (!remainder.content.size) return true;

      // Serialize back to markdown before asking isBodyEmpty: removeSignature
      // matches the body against the markdown signature, so comparing it with
      // the document's plain text would never strip a formatted or
      // multi-paragraph signature.
      const remainderDoc = state.schema.node('doc', null, remainder.content);
      return this.isBodyEmpty(
        MessageMarkdownSerializer.serialize(remainderDoc)
      );
    },
    isBodyEmpty(content) {
      // Shares one definition of "empty draft" with the plain-textarea composer
      // (ResizableTextArea) so the two cannot drift apart.
      return isBodyEmptyOrSignatureOnly(content, this.signature);
    },
    handleEmptyBodyWithSignature() {
      const { schema, tr } = this.state;

      const paragraph = schema.nodes.paragraph.create();
      const paragraphTransaction = tr.insert(0, paragraph);
      this.editorView.dispatch(paragraphTransaction);

      this.focusEditorInputField('start');
    },
    createEditorView() {
      this.editorView = new EditorView(this.$refs.editor, {
        state: this.state,
        dispatchTransaction: tx => {
          this.state = this.state.apply(tx);
          this.editorView.updateState(this.state);
          this.emitOnChange();
          this.resizeEditor();
        },
        handleDOMEvents: {
          keyup: () => {
            this.onKeyup();
          },
          keydown: (view, event) => {
            this.onKeydown(event);
          },
          focus: () => {
            this.onFocus();
          },
          click: () => {
            this.isEditorMouseFocusedOnAnImage();
          },
          blur: () => {
            this.onBlur();
          },
          paste: (view, event) => {
            const data = Array.from(event.clipboardData.files);
            if (data.length === 0) {
              return;
            }

            // Files are present, so suppress ProseMirror's own paste handling —
            // which is what this handler always did, on every path. Some
            // clipboards carry a text/html twin next to the file (copying an
            // image off a web page); without this the editor would insert that
            // remote <img> while ReplyBox attaches the file as well.
            // Propagation is NOT stopped here: ReplyBox's document-level
            // listener still has to see the event to create the attachment.
            event.preventDefault();

            // Narrower than image/*: the mailer only embeds the types in this
            // list, and anything else pasted inline would reach the contact as a
            // blocked remote image with no attachment behind it.
            const images = this.enableInlineImagePaste
              ? data.filter(file =>
                  INLINE_EMBEDDABLE_IMAGE_TYPES.includes(file.type)
                )
              : [];

            // Not opted in, or a mix the mailer cannot embed: the attachment
            // path handles it, exactly as before this feature existed.
            if (images.length === 0 || images.length !== data.length) {
              return;
            }

            this.uploadPastedImages(images);
            // ReplyBox ALSO listens for paste on `document` and turns files into
            // attachments. Without stopping propagation the pasted image would be
            // inserted inline and attached, reaching the contact twice.
            event.stopPropagation();
          },
        },
      });
    },
    isEditorMouseFocusedOnAnImage() {
      if (!this.showImageResizeToolbar) {
        return;
      }
      this.selectedImageNode = document.querySelector(
        'img.ProseMirror-selectednode'
      );
      if (this.selectedImageNode) {
        this.isImageNodeSelected = !!this.selectedImageNode;
        this.setToolbarPosition();
      } else {
        this.isImageNodeSelected = false;
      }
    },
    setToolbarPosition() {
      const editorRect = this.editorRoot.getBoundingClientRect();
      const rect = this.selectedImageNode.getBoundingClientRect();
      this.toolbarPosition = {
        top: `${rect.top - editorRect.top - 30}px`,
        left: `${rect.left - editorRect.left - 4}px`,
      };
    },
    setURLWithQueryAndImageSize(size) {
      if (!this.showImageResizeToolbar) {
        return;
      }
      setURLWithQueryAndSize(this.selectedImageNode, size, this.editorView);
      this.isImageNodeSelected = false;
    },
    updateImgToolbarOnDelete() {
      if (this.selectedImageNode) {
        const hasImgSelectedNode = document.querySelector(
          'img.ProseMirror-selectednode'
        );
        if (!hasImgSelectedNode) {
          this.isImageNodeSelected = false;
        }
      }
    },
    isEnterToSendEnabled() {
      return this.isEditorHotKeyEnabled('enter');
    },
    isCmdPlusEnterToSendEnabled() {
      return this.isEditorHotKeyEnabled('cmd_enter');
    },
    getKeyboardEvents() {
      return {
        'Alt+KeyP': {
          action: () => {
            this.focusEditorInputField();
          },
          allowOnFocusedInput: true,
        },
        'Alt+KeyL': {
          action: () => {
            this.focusEditorInputField();
          },
          allowOnFocusedInput: true,
        },
      };
    },
    focusEditorInputField(pos = 'end') {
      const { tr } = this.editorView.state;

      const selection =
        pos === 'end' ? Selection.atEnd(tr.doc) : Selection.atStart(tr.doc);

      this.editorView.dispatch(tr.setSelection(selection));
      this.editorView.focus();
    },
    insertSpecialContent(type, content) {
      if (!this.editorView) {
        return;
      }

      let { node, from, to } = getContentNode(
        this.editorView,
        type,
        content,
        this.range,
        this.variables
      );

      if (!node) return;

      this.insertNodeIntoEditor(node, from, to);

      const event_map = {
        mention: CONVERSATION_EVENTS.USED_MENTIONS,
        cannedResponse: CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE,
        variable: CONVERSATION_EVENTS.INSERTED_A_VARIABLE,
        emoji: CONVERSATION_EVENTS.INSERTED_AN_EMOJI,
      };

      this.$track(event_map[type]);
    },
    openFileBrowser() {
      this.$refs.imageUpload.click();
    },
    onFileChange() {
      const file = this.$refs.imageUpload.files[0];
      if (checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
        this.uploadImageToStorage(file);
      } else {
        useAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_SIZE_ERROR',
            {
              size: MAXIMUM_FILE_UPLOAD_SIZE,
            }
          )
        );
      }

      this.$refs.imageUpload.value = '';
    },
    // Sequential on purpose: each upload inserts at the cursor when its own
    // promise resolves, so firing them concurrently drops the images in order of
    // completion rather than the order the agent copied them.
    async uploadPastedImages(images) {
      // Captured once for the whole batch, not per file: these images were
      // copied together for the conversation that was open then. Re-reading the
      // key each iteration would let the files after a switch compare against
      // the NEW conversation, pass, and be inserted there.
      const pastedInto = this.contextKey;

      for (const file of images) {
        // Abandoned as soon as the agent moves on, and the remaining files go
        // with it: the composer they were meant for is gone, and anything this
        // loop still does would land in someone else's reply. Losing the images
        // is the lesser outcome. Checked before starting, because a switch
        // between iterations would otherwise upload straight into the new
        // conversation.
        if (this.contextKey !== pastedInto) {
          break;
        }

        let inserted = false;
        if (checkFileSizeLimit(file, MAXIMUM_ATTACHMENT_UPLOAD_SIZE)) {
          // eslint-disable-next-line no-await-in-loop
          inserted = await this.uploadImageToStorage(file, { notify: false });
        }

        // And again after the upload, which is where the switch usually happens.
        // The parent attaches to whatever is open NOW, so handing the file back
        // would put one contact's image in the composer of another.
        if (this.contextKey !== pastedInto) {
          break;
        }

        // The event was already consumed synchronously, so a failure here would
        // leave the image in neither the body nor the attachments. Hand the file
        // back to the parent, which owns the attachment path.
        if (!inserted) {
          this.$emit('inlineImagePasteFailed', file);
        }
      }
    },

    // `notify` exists so the paste path can stay silent. The success copy below
    // belongs to the profile signature editor, which is not one of this
    // component's callers (reply box, notes, campaigns, canned responses,
    // automations) — worth replacing with neutral copy in a follow-up.
    // Returns whether the image actually made it into the editor, so a caller
    // that already consumed the paste event can fall back to attaching the file.
    async uploadImageToStorage(file, { notify = true } = {}) {
      const startedIn = this.contextKey;
      try {
        const { fileUrl } = await uploadFile(file);
        if (!fileUrl) {
          // Same silence as the discard below had: the toolbar and drag paths
          // ignore the return value, so an upload that comes back without a URL
          // left the agent with nothing at all and no reason why.
          if (notify) {
            useAlert(
              this.$t(
                'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_ERROR'
              )
            );
          }
          return false;
        }
        // The agent opened another conversation while this was in flight. The
        // editor on screen now belongs to that one, so inserting here would drop
        // the image into a reply meant for a different contact.
        if (this.contextKey !== startedIn) {
          // The toolbar and drag paths ignore the return value, so without a
          // word here the image would simply never appear — no picture, no
          // attachment, no error. The paste path passes `notify: false` and
          // handles its own discard.
          if (notify) {
            useAlert(
              this.$t(
                'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_ERROR'
              )
            );
          }
          return false;
        }
        this.onImageInsertInEditor(fileUrl);
        if (notify) {
          useAlert(
            this.$t(
              'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_SUCCESS'
            )
          );
        }
        return true;
      } catch (error) {
        // Silent when the caller asked not to notify: it has its own fallback
        // and an "upload failed" toast next to a file that did get attached
        // would only confuse.
        if (notify) {
          useAlert(
            this.$t(
              'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_ERROR'
            )
          );
        }
        return false;
      }
    },
    onImageInsertInEditor(fileUrl) {
      const { tr } = this.editorView.state;

      const insertData = findNodeToInsertImage(this.editorView.state, fileUrl);

      if (insertData) {
        this.editorView.dispatch(
          tr.insert(insertData.pos, insertData.node).scrollIntoView()
        );
        this.focusEditorInputField();
      }
    },

    emitOnChange() {
      this.$emit('input', this.contentFromEditor);
    },

    getMaxHeightPx() {
      if (!this.maxHeight) return Infinity;
      if (typeof this.maxHeight === 'number') return this.maxHeight;
      if (typeof this.maxHeight === 'string' && this.maxHeight.endsWith('%')) {
        return (parseFloat(this.maxHeight) / 100) * window.innerHeight;
      }
      return parseFloat(this.maxHeight) || Infinity;
    },

    resizeEditor() {
      this.$nextTick(() => {
        const el = this.$refs.editorRoot?.querySelector('.ProseMirror');
        if (!el) return;

        const maxHeightPx = this.getMaxHeightPx();

        // Controlled height (manually resized via the drag handle): keep the
        // pinned height until the content overflows it, then expand up to the
        // max and release manual control back to auto-grow.
        if (this.height !== null) {
          if (el.scrollHeight > this.height) {
            el.style.height = `${Math.min(el.scrollHeight, maxHeightPx)}px`;
            this.$emit('releaseHeight');
          }
          return;
        }

        // Auto mode. Only take over sizing when a finite max is configured
        // (opt-in via :max-height, e.g. the reply box). Editors without a
        // max keep their CSS-governed height (.ProseMirror-woot-style cap +
        // internal scroll), so this stays scoped to consumers that want it.
        if (!Number.isFinite(maxHeightPx)) return;

        // Reset to 'auto' to measure the true content height, then grow (and
        // shrink) up to the max before letting the editor scroll internally.
        // Bail while hidden (scrollHeight 0) to avoid pinning a collapsed height.
        el.style.height = 'auto';
        if (!el.scrollHeight) return;
        el.style.maxHeight = `${maxHeightPx}px`;
        el.style.height = `${Math.min(el.scrollHeight, maxHeightPx)}px`;
      });
    },

    hideMentions() {
      this.showUserMentions = false;
    },
    handleLineBreakWhenEnterToSendEnabled(event) {
      if (
        hasPressedEnterAndNotCmdOrShift(event) &&
        this.isEnterToSendEnabled() &&
        !this.overrideLineBreaks
      ) {
        event.preventDefault();
      }
    },
    handleLineBreakWhenCmdAndEnterToSendEnabled(event) {
      if (
        hasPressedCommandAndEnter(event) &&
        this.isCmdPlusEnterToSendEnabled() &&
        !this.overrideLineBreaks
      ) {
        event.preventDefault();
      }
    },
    onKeyup() {
      this.typingIndicator.start();
      this.updateImgToolbarOnDelete();
    },
    onKeydown(event) {
      if (this.isEnterToSendEnabled()) {
        this.handleLineBreakWhenEnterToSendEnabled(event);
      }
      if (this.isCmdPlusEnterToSendEnabled()) {
        this.handleLineBreakWhenCmdAndEnterToSendEnabled(event);
      }
    },
    onBlur() {
      this.typingIndicator.stop();
      this.$emit('blur');
    },
    onFocus() {
      this.$emit('focus');
    },
    insertContentIntoEditor(content, defaultFrom = 0) {
      const from = defaultFrom || this.editorView.state.selection.from || 0;
      let node = new MessageMarkdownTransformer(messageSchema).parse(content);

      this.insertNodeIntoEditor(node, from, undefined);
    },
    insertNodeIntoEditor(node, from = 0, to = 0) {
      this.state = insertAtCursor(this.editorView, node, from, to);
      this.emitOnChange();
      this.$nextTick(() => {
        scrollCursorIntoView(this.editorView);
      });
    },
  },
};
</script>

<template>
  <div ref="editorRoot" class="relative editor-root">
    <TagAgents
      v-if="showUserMentions && (isPrivate || mentionItems)"
      :search-key="mentionSearchKey"
      :items="mentionItems"
      @click="content => insertSpecialContent('mention', content)"
    />
    <CannedResponse
      v-if="shouldShowCannedResponses"
      :search-key="cannedSearchTerm"
      @click="content => insertSpecialContent('cannedResponse', content)"
      @insertAttachments="e => $emit('insertAttachments', e)"
    />
    <VariableList
      v-if="shouldShowVariables"
      :search-key="variableSearchTerm"
      :variable-keys="variableKeys"
      @click="content => insertSpecialContent('variable', content)"
    />
    <KeyboardEmojiSelector
      v-if="showEmojiMenu"
      :search-key="emojiSearchTerm"
      @click="emoji => insertSpecialContent('emoji', emoji)"
    />
    <input
      ref="imageUpload"
      type="file"
      accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
      hidden
      :aria-label="
        $t(
          'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_ARIA_LABEL'
        )
      "
      @change="onFileChange"
    />
    <div ref="editor" />
    <div
      v-show="isImageNodeSelected && showImageResizeToolbar"
      class="absolute z-10 shadow-md rounded-[4px] flex gap-1 py-1 px-1 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-50"
      :style="{
        top: toolbarPosition.top,
        left: toolbarPosition.left,
      }"
    >
      <button
        v-for="size in sizes"
        :key="size.nameKey"
        type="button"
        class="text-xs font-medium rounded-[4px] border border-solid border-slate-200 dark:border-slate-600 px-1.5 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-800"
        @click="setURLWithQueryAndImageSize(size)"
      >
        {{ $t(size.nameKey) }}
      </button>
    </div>
    <slot name="footer" />
  </div>
</template>

<style lang="scss">
@import '~@chatwoot/prosemirror-schema/src/styles/base.scss';

.editor-root {
  @apply w-full relative pt-0 outline-none;
}

.ProseMirror-woot-style {
  @apply overflow-auto min-h-[5rem] max-h-[7.5rem];
}

.ProseMirror-prompt {
  @apply z-[9999] bg-slate-25 dark:bg-slate-700 rounded-md border border-solid border-slate-75 dark:border-slate-800 shadow-lg;

  h5 {
    @apply dark:text-slate-25 text-slate-800;
  }
}

.ProseMirror-menubar-wrapper {
  @apply flex flex-col;

  .ProseMirror-menubar {
    min-height: var(--space-two) !important;
    @apply py-0 bg-white dark:bg-gray-gray-14 text-slate-700 dark:text-slate-100 border-gray-gray-8 border-b flex gap-4 pb-4;

    .ProseMirror-menu-active {
      @apply bg-dark-dark-2 dark:bg-white text-white dark:text-dark-dark-2;
    }

    .ProseMirror-icon {
      padding: 0px;

      svg {
        @apply h-4 !w-4;
      }
    }
  }

  > .ProseMirror {
    @apply p-0 break-words text-slate-800 dark:text-slate-100;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      @apply text-slate-800 dark:text-slate-100;
    }

    blockquote {
      @apply border-slate-400 dark:border-slate-500;

      p {
        @apply text-slate-600 dark:text-slate-400;
      }
    }

    ol li {
      @apply list-item list-decimal;
    }
  }
}

.is-private {
  .prosemirror-mention-node {
    @apply font-medium bg-gray-gray-2 dark:bg-gray-gray-11 text-slate-900 dark:text-slate-25 py-0 px-1;
  }

  .ProseMirror-menubar-wrapper {
    .ProseMirror-menubar {
      @apply bg-gray-gray-2 dark:bg-gray-gray-11 text-slate-700 dark:text-dark-dark-7 border-white border-b;
    }

    > .ProseMirror {
      @apply text-slate-800 dark:text-slate-25;

      p {
        @apply text-slate-800 dark:text-slate-25;
      }
    }
  }
}

.editor-wrap {
  @apply mb-4;
}

.message-editor {
  @apply border border-solid border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-md py-0 px-1 mb-0;
}

.editor_warning {
  @apply border border-solid border-red-400 dark:border-red-400;
}

.editor-warning__message {
  @apply text-red-400 dark:text-red-400 font-normal text-sm pt-1 pb-0 px-0;
}
</style>
